import { GzInputWebComponent } from './GzInputWebComponent';
import { html, nothing, unsafeCSS } from 'lit';
import { property, query, state, customElement } from 'lit/decorators.js';
import { InputEvents } from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { styles as textareaStyles } from '../view/input-textarea.scss';

@customElement('gz-textarea')
export class GzTextareaWebComponent extends GzInputWebComponent {
  static get styles() {
    return [...GzInputWebComponent.styles, unsafeCSS(`${textareaStyles}`)];
  }

  @property({ type: Number, attribute: true, reflect: true })
  rows = 3;

  @property({ type: Number, attribute: true, reflect: true })
  maxChars = 100;

  @property({ type: Number, attribute: true, reflect: true })
  maxRows: number;

  @property({ type: Boolean, attribute: true, reflect: true })
  resizableByContent = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasCounter = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  showErrorIcon = false;

  /**
   * This needs to be used alongside validators
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  allowBeyondMaxChars = false;

  value = '';

  @query('.input--textarea')
  textareaInput!: HTMLElement;

  @state()
  textareaInputInitialHeight = 34;

  /**
   * This is the default value for the counterMaxChars property that browser supports
   */
  private defaultCounterMaxChars = 524288;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(InputEvents.CHANGE, this.handleInputChange);
    this.addEventListener(InputEvents.CHANGE, this.adjustSizeOnType);
  }

  disconnectedCallback() {
    this.removeEventListener(InputEvents.CHANGE, this.handleInputChange);
    this.addEventListener(InputEvents.CHANGE, this.adjustSizeOnType);
    super.disconnectedCallback();
  }

  updated() {
    this.keyCounter();
  }

  render() {
    const classes = {
      input: true,
      'textarea-input-wrapper': true,
      invalid: this.invalid,
      'floating-label': this.value,
      'has-label': this.label,
    };

    return html`
      <div class="${classMap(classes)}">
        ${this.renderInputLabel()}
        <textarea
          class="input--field input--textarea"
          .rows="${this.rows}"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          aria-label="${this.label}"
          .value="${this.value}"
          .placeholder="${this.placeholder}"
          @keyup="${this.onKeyUp}"
          @keydown="${() => this.setActive(true)}"
          @change="${this.onUserInput}"
          @blur="${this.onInputBlur}"
          @input="${this.handleInputEvent}"
          maxlength="${this.getMaxLength()}"
          part="textarea"
        >
        </textarea>
        ${this.showErrorIcon ? this.renderInvalidIcon() : nothing}
      </div>
      ${this.renderCounter()}
    `;
  }

  private getMaxLength(): number | undefined {
    if (!this.hasCounter) {
      return;
    }

    if (this.allowBeyondMaxChars) {
      return this.defaultCounterMaxChars;
    }

    return this.maxChars;
  }

  protected onKeyUp(event: KeyboardEvent) {
    super.onKeyUp(event);
    this.keyCounter();
  }

  protected keyCounter() {
    super.keyCounter();
    this.inputMaxRangeColor(this.allowBeyondMaxChars ? this.maxChars + 1 : this.maxChars);
  }

  private adjustSizeOnType() {
    if (!this.resizableByContent) {
      return;
    }

    setTimeout(() => {
      this.textareaInput.style.height = 'auto';
      this.textareaInput.style.height =
        this.textareaInput.scrollHeight < this.textareaInputInitialHeight
          ? this.textareaInput.scrollHeight + 'px'
          : this.textareaInput.scrollHeight + 4 + 'px';
    }, 0);
  }
}
